import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDoubleLeft, CaretDoubleRight, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface Props {
  value?: string | undefined;    // YYYY-MM-DD; empty / undefined = no selection
  onSelect: (date: string) => void;
  min?: string | undefined;      // YYYY-MM-DD; days before this are disabled
  max?: string | undefined;      // YYYY-MM-DD; days after this are disabled
  locale?: string | undefined;   // BCP-47 tag for month/weekday names
}

// Number of years shown at once in the year-grid view (4 cols × 4 rows).
// Far enough across the screen to scan, narrow enough to fit the popover.
const YEARS_PER_PAGE = 16;

type Mode = 'days' | 'years';

export function Calendar({ value, onSelect, min, max, locale = 'en' }: Props) {
  const { t } = useTranslation('common');
  const selected = value ? parseYMD(value) : null;

  const [viewYear, setViewYear] = useState(
    () => selected?.y ?? new Date().getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    () => selected?.m ?? new Date().getMonth(),
  );
  // Mode toggles the body between the day grid and a year grid. Clicking the
  // year/month header flips to year mode; picking a year flips back. Avoids
  // the "click prev-year 35 times" cliff on birth-date pickers.
  const [mode, setMode] = useState<Mode>('days');
  // First year shown when in year mode. Initialised to the page containing
  // viewYear so opening the picker lands on a familiar page.
  const [yearPageStart, setYearPageStart] = useState(
    () => Math.floor((selected?.y ?? new Date().getFullYear()) / YEARS_PER_PAGE) * YEARS_PER_PAGE,
  );

  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(viewYear, viewMonth));

  // Weekday short names Mon → Sun (ISO week, Mon = first column).
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2024-01-01 was a Monday — use that week as an anchor.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [locale]);

  // Day cells: null = empty leading/trailing slot.
  const cells = useMemo(() => {
    const firstDow = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
    const leading = firstDow === 0 ? 6 : firstDow - 1;         // shift to Mon=0
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const arr: (number | null)[] = Array(leading).fill(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [viewYear, viewMonth]);

  function prevMonth() {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  }
  function prevYear() { setViewYear((y) => y - 1); }
  function nextYear() { setViewYear((y) => y + 1); }

  function openYearPicker() {
    setYearPageStart(Math.floor(viewYear / YEARS_PER_PAGE) * YEARS_PER_PAGE);
    setMode('years');
  }
  function pickYear(y: number) {
    setViewYear(y);
    setMode('days');
  }

  function handleSelect(day: number) {
    onSelect(toYMDString(viewYear, viewMonth, day));
  }

  function disabled(day: number) {
    const d = toYMDString(viewYear, viewMonth, day);
    return (!!min && d < min) || (!!max && d > max);
  }
  function yearDisabled(y: number) {
    // A year is disabled only if its entire span sits outside min/max.
    // (A partial overlap stays clickable so day-mode can show the in-range
    // days.)
    const earliest = `${y}-01-01`;
    const latest = `${y}-12-31`;
    if (min && latest < min) return true;
    if (max && earliest > max) return true;
    return false;
  }
  function isSelected(day: number) {
    return !!selected && selected.y === viewYear && selected.m === viewMonth && selected.d === day;
  }
  function isToday(day: number) {
    const t = new Date();
    return t.getFullYear() === viewYear && t.getMonth() === viewMonth && t.getDate() === day;
  }

  const yearsView = mode === 'years';
  const yearPageEnd = yearPageStart + YEARS_PER_PAGE - 1;
  const yearGridLabel = `${yearPageStart} – ${yearPageEnd}`;

  return (
    <div className="select-none p-1">
      {/* Month + year navigation. In days mode: double chevrons step a year,
          single chevrons step a month, the centre label flips to year mode
          on click. In years mode: chevrons step a full year page and the
          label shows the visible year range. */}
      <div className="mb-3 flex items-center justify-between gap-1">
        <div className="flex items-center gap-0.5">
          {yearsView ? (
            <button
              type="button"
              onClick={() => setYearPageStart((s) => s - YEARS_PER_PAGE)}
              className="flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('calendar.prevYearPage')}
            >
              <CaretDoubleLeft className="h-4 w-4" weight="bold" />
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={prevYear}
                className="flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('calendar.prevYear')}
              >
                <CaretDoubleLeft className="h-4 w-4" weight="bold" />
              </button>
              <button
                type="button"
                onClick={prevMonth}
                className="flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('calendar.prevMonth')}
              >
                <CaretLeft className="h-4 w-4" weight="bold" />
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => (yearsView ? setMode('days') : openYearPicker())}
          className="rounded-pill px-2 py-0.5 font-heading text-sm font-semibold capitalize transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={yearsView ? t('calendar.closeYearPicker') : t('calendar.openYearPicker')}
        >
          {yearsView ? yearGridLabel : monthLabel}
        </button>
        <div className="flex items-center gap-0.5">
          {yearsView ? (
            <button
              type="button"
              onClick={() => setYearPageStart((s) => s + YEARS_PER_PAGE)}
              className="flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('calendar.nextYearPage')}
            >
              <CaretDoubleRight className="h-4 w-4" weight="bold" />
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={nextMonth}
                className="flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('calendar.nextMonth')}
              >
                <CaretRight className="h-4 w-4" weight="bold" />
              </button>
              <button
                type="button"
                onClick={nextYear}
                className="flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('calendar.nextYear')}
              >
                <CaretDoubleRight className="h-4 w-4" weight="bold" />
              </button>
            </>
          )}
        </div>
      </div>

      {yearsView ? (
        <div className="grid grid-cols-4 gap-1 px-1 pb-1">
          {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map((y) => {
            const isViewYear = y === viewYear;
            const isCurrent = y === new Date().getFullYear();
            const isDisabled = yearDisabled(y);
            return (
              <button
                key={y}
                type="button"
                disabled={isDisabled}
                onClick={() => pickYear(y)}
                className={cn(
                  'flex h-9 items-center justify-center rounded-pill text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isViewYear
                    ? 'bg-primary font-bold text-primary-foreground'
                    : isCurrent
                      ? 'border border-primary text-foreground hover:bg-muted'
                      : 'text-foreground hover:bg-muted',
                  isDisabled && 'cursor-not-allowed opacity-30',
                )}
              >
                {y}
              </button>
            );
          })}
        </div>
      ) : (
        <>
          {/* Weekday headers */}
          <div className="mb-1 grid grid-cols-7">
            {weekdays.map((wd) => (
              <span
                key={wd}
                className="py-1 text-center font-heading text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground"
              >
                {wd.slice(0, 2)}
              </span>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, i) => (
              <div key={i} className="flex items-center justify-center">
                {day !== null && (
                  <button
                    type="button"
                    disabled={disabled(day)}
                    onClick={() => handleSelect(day)}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-pill text-sm transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isSelected(day)
                        ? 'bg-primary font-bold text-primary-foreground'
                        : isToday(day)
                          ? 'border border-primary text-foreground hover:bg-muted'
                          : 'text-foreground hover:bg-muted',
                      disabled(day) && 'cursor-not-allowed opacity-30',
                    )}
                  >
                    {day}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function parseYMD(s: string): { y: number; m: number; d: number } | null {
  const [y, mo, d] = s.split('-').map(Number);
  if (y === undefined || mo === undefined || d === undefined || isNaN(y) || isNaN(mo) || isNaN(d)) return null;
  return { y, m: mo - 1, d };
}

function toYMDString(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
